import { useState } from "react";
import { ERROR_MESSAGES } from "../../../constant/constant";
import { TENANT_DOMAIN } from "../../../utils/domain";
import { useAxios } from "../../../utils/useAxios";

/**
 * @typedef {object} QuestItem
 * @property {number} quest_id - ID of the quest
 * @property {string} quest_name - Name of the quest
 * @property {string} episodes - Episodes completed (e.g., "0/5")
 * @property {number} percentage - Completion percentage
 * @property {string|null} due_date - Due date
 * @property {string} status - Status of the quest (e.g., "Pending", "In Progress", "Completed")
 */

/**
 * @typedef {object} StudentDetail
 * @property {number} id - Student ID
 * @property {string} name - Student name
 * @property {string} email - Student email
 * @property {string} username - Student username
 * @property {string} age - Student age
 * @property {string} student_code - Student code
 * @property {number} overall_progress - Overall progress percentage
 * @property {number} avg_quiz_score - Average quiz score
 * @property {number} total_quests_assigned - Total quests assigned
 */

/**
 * @typedef {Object<string, QuestItem[]>} QuestsByCategory
 * Quests grouped by category name. The key is the category name and the value is an array of quests.
 * Example: { "Adventure": [QuestItem, QuestItem], "Educational": [QuestItem] }
 */

/**
 * @typedef {object} StudentDetailByIdData
 * @property {StudentDetail} student - Student details
 * @property {QuestsByCategory} quests_by_category - Quests grouped by category (can have multiple categories)
 */

/**
 * Custom hook to fetch student detail by ID for a specific sub-school
 */
export const useGetStudentDetailByIdForSubSchool = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const { axiosInstance } = useAxios();

    const [studentData, setStudentData] = useState(
        /** @type {StudentDetailByIdData | null | undefined} */(undefined)
    );

    /**
     * Fetch student detail by ID for a specific sub-school
     * @async
     * @param {number|string} schoolId - ID of the sub-school
     * @param {number|string} studentId - ID of the student
     * @returns {Promise<void>}
     */
    const getStudentDetailById = async (schoolId, studentId) => {
        if (!schoolId) {
            setError("School ID is required");
            setLoading(false);
            return;
        }

        if (!studentId) {
            setError("Student ID is required");
            setLoading(false);
            return;
        }

        setLoading(true);
        setError("");

        try {
            const url = `${TENANT_DOMAIN}/organization/admin/reports/sub-school/${schoolId}/student/${studentId}`;
            const response = await axiosInstance.get(url);
            const data = response.data;

            setStudentData(data);
        } catch (err) {
            setStudentData(null);
            if (err?.response?.status === 500) {
                setError(ERROR_MESSAGES.INTERNAL_SERVER);
            } else {
                setError(err?.response?.data?.message || ERROR_MESSAGES.UNKNOWN);
            }
        } finally {
            setLoading(false);
        }
    };

    /**
     * Reset the state to initial values
     */
    const resetState = () => {
        setStudentData(undefined);
        setError("");
        setLoading(true);
    };

    /**
     * Get all quest categories from the quests_by_category object
     * @returns {string[]} Array of category names
     */
    const getQuestCategories = () => {
        if (!studentData?.quests_by_category) return [];
        return Object.keys(studentData.quests_by_category);
    };

    /**
     * Get quests for a specific category
     * @param {string} category - Category name
     * @returns {QuestItem[]} Array of quests for the category
     */
    const getQuestsByCategory = (category) => {
        if (!studentData?.quests_by_category || !category) return [];
        return studentData.quests_by_category[category] || [];
    };

    /**
     * Get total number of quests across all categories
     * @returns {number} Total number of quests
     */
    const getTotalQuestsCount = () => {
        if (!studentData?.quests_by_category) return 0;
        return Object.values(studentData.quests_by_category).reduce(
            (total, quests) => total + quests.length,
            0
        );
    };

    return {
        loading,
        error,
        studentData,
        getStudentDetailById,
        resetState,
        getQuestCategories,
        getQuestsByCategory,
        getTotalQuestsCount,
    };
};